import classes from './ChatWidget.module.scss'
import { ChatRepresentativeCompany } from './types'
import { IconMail, IconPhone } from './icons'

interface Props {
  representatives: ChatRepresentativeCompany[]
}

function phoneHref(phoneNumber: string) {
  const normalized = phoneNumber.replace(/[^\d+]/g, '')
  return normalized ? `tel:${normalized}` : undefined
}

/**
 * Oturum kapsamındaki temsilcileri salt-okuma iletişim listesinde gösterir.
 *
 * Faz-44 (spec §2): kompakt görünüm — kişi başına tek satır: ad · telefon/e-posta bağlantıları;
 * telefon numarası okunabilsin diye ikonun yanında yazılır, uzun e-posta adresi yalnız ikondur.
 * Faz-46: liste HER ZAMAN şirket bazında gruplanır (şirket adı grup başlığıdır, satırda tekrarlanmaz)
 * ve her kişinin üstünde ne temsilcisi olduğu küçük bir başlıkla yazılır. Adı olmayan kişide tür
 * zaten ad yerine gösterildiği için başlık tekrarlanmaz.
 */
export default function RepresentativesCard({ representatives }: Props) {
  if (representatives.length === 0) return null

  return (
    <div className={classes.representativeList} aria-label='Müşteri temsilcileri'>
      {representatives.map((company, companyIndex) => (
        <section
          key={`${company.companyId ?? company.companyCode ?? company.companyName}-${companyIndex}`}
          className={classes.representativeGroup}
        >
          <h4
            className={classes.representativeCompanyHeader}
            title={company.companyCode ? `${company.companyName} (${company.companyCode})` : company.companyName}
          >
            {company.companyName}
          </h4>
          <ul className={classes.representativeContacts}>
            {company.contacts.map((contact, contactIndex) => {
              const displayName = contact.name ?? contact.type
              return (
                <li
                  key={`${contact.email ?? contact.name ?? contact.type}-${contactIndex}`}
                  className={classes.representativeRow}
                >
                  {contact.name && contact.type && (
                    <span className={classes.representativeType}>{contact.type}</span>
                  )}
                  <span className={classes.representativeName}>{displayName}</span>

                  {(contact.email || contact.phoneNumbers.length > 0) && (
                    <span className={classes.representativeLinks}>
                      {contact.phoneNumbers.map((phoneNumber, phoneIndex) => {
                        const href = phoneHref(phoneNumber)
                        return href ? (
                          <a
                            key={`${phoneNumber}-${phoneIndex}`}
                            href={href}
                            className={classes.representativeLink}
                            title={phoneNumber}
                            aria-label={`${displayName} telefon: ${phoneNumber}`}
                          >
                            <IconPhone />
                            <span>{phoneNumber}</span>
                          </a>
                        ) : (
                          <span key={`${phoneNumber}-${phoneIndex}`} className={classes.representativeLink}>
                            {phoneNumber}
                          </span>
                        )
                      })}
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className={`${classes.representativeLink} ${classes.representativeIconLink}`}
                          title={contact.email}
                          aria-label={`${displayName} e-posta: ${contact.email}`}
                        >
                          <IconMail />
                        </a>
                      )}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}
